import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';
import MergeChatLogo from '../components/MergeChatLogo';

// ── Helpers ────────────────────────────────────────────
function fmt(n) { return Number(n || 0).toLocaleString(); }
function pct(part, total) { return total > 0 ? Math.round((part / total) * 100) : 0; }

// ── Stat Card ──────────────────────────────────────────
function StatCard({ icon, label, value, trend, color }) {
  return (
    <div className="dash-stat" style={{ borderTop: `3px solid ${color}` }}>
      <div className="dash-stat-top">
        <span className="dash-stat-icon">{icon}</span>
        {trend !== null && trend !== undefined && (
          <span className={`dash-stat-trend ${trend >= 0 ? 'trend-up' : 'trend-down'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="dash-stat-value">{fmt(value)}</div>
      <div className="dash-stat-label">{label}</div>
    </div>
  );
}

// ── Bar Chart (messages by day) ────────────────────────
function BarChart({ data, days, startDate }) {
  // Fill missing days — anchor to startDate+days-1 if custom range, else today
  const filled = [];
  const base = startDate ? dayjs(startDate).add(days - 1, 'day') : dayjs();
  for (let i = days - 1; i >= 0; i--) {
    const day = base.subtract(i, 'day').format('YYYY-MM-DD');
    const found = data.find(d => d.day === day);
    filled.push({ day, inbound: Number(found?.inbound || 0), outbound: Number(found?.outbound || 0) });
  }
  const maxVal = Math.max(...filled.map(d => d.inbound + d.outbound), 1);
  const showLabel = days <= 14;

  return (
    <div className="bar-chart-wrap">
      <div className="bar-chart">
        {filled.map(d => {
          const total = d.inbound + d.outbound;
          const heightPct = (total / maxVal) * 100;
          const inPct = total > 0 ? (d.inbound / total) * 100 : 0;
          return (
            <div key={d.day} className="bar-col" title={`${dayjs(d.day).format('D MMM')}: ${d.inbound} เข้า · ${d.outbound} ออก`}>
              <div className="bar-body" style={{ height: `${Math.max(heightPct, 2)}%` }}>
                <div className="bar-segment bar-in"  style={{ height: `${inPct}%` }} />
                <div className="bar-segment bar-out" style={{ height: `${100 - inPct}%` }} />
              </div>
              {showLabel && (
                <div className="bar-label">{dayjs(d.day).format('D/M')}</div>
              )}
            </div>
          );
        })}
      </div>
      <div className="bar-legend">
        <span className="bar-legend-dot" style={{ background: 'var(--accent)' }} /> ข้อควา��เข้า
        <span className="bar-legend-dot" style={{ background: 'var(--accent-mid)', marginLeft: 12 }} /> ข้อความออก
      </div>
    </div>
  );
}

// ── Peak Hours ─────────────────────────────────────────
function PeakHours({ data }) {
  const arr = Array.from({ length: 24 }, (_, h) => {
    const f = data.find(d => Number(d.hour) === h);
    return { hour: h, count: Number(f?.count || 0) };
  });
  const maxCount = Math.max(...arr.map(d => d.count), 1);
  const peakHour = arr.reduce((a, b) => b.count > a.count ? b : a, arr[0]);

  return (
    <div>
      <div className="peak-chart">
        {arr.map(d => {
          const h = (d.count / maxCount) * 100;
          return (
            <div key={d.hour} className="peak-col" title={`${d.hour}:00 — ${d.count} ข้อความ`}>
              <div
                className="peak-bar"
                style={{ height: `${Math.max(h, 2)}%`, opacity: 0.25 + (h / 100) * 0.75 }}
              />
              {d.hour % 6 === 0 && <div className="peak-label">{d.hour}:00</div>}
            </div>
          );
        })}
      </div>
      {peakHour.count > 0 && (
        <div className="peak-note">ชั่วโมงยอดนิยม: {peakHour.hour}:00–{peakHour.hour + 1}:00 ({fmt(peakHour.count)} ข้อความ)</div>
      )}
    </div>
  );
}

// ── OA Breakdown ───────────────────────────────────────
function OaBreakdown({ data }) {
  const maxConv = Math.max(...data.map(d => Number(d.conversations)), 1);
  if (!data.length) return <div className="dash-empty">ไม่มีข้อมูล</div>;
  return (
    <div className="oa-break-list">
      {data.map(oa => (
        <div key={oa.id} className="oa-break-row">
          <div className="oa-break-dot" style={{ background: oa.color }} />
          <div className="oa-break-name">{oa.name}</div>
          <div className="oa-break-bar-wrap">
            <div className="oa-break-bar" style={{ width: `${pct(Number(oa.conversations), maxConv)}%`, background: oa.color + 'aa' }} />
          </div>
          <div className="oa-break-count">{fmt(oa.conversations)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Status Distribution ────────────────────────────────
function StatusChart({ data }) {
  const map = { open: { label: 'เปิด', color: 'var(--accent)' }, pending: { label: 'รอดำเนินการ', color: 'var(--orange)' }, closed: { label: 'ปิดแล้ว', color: 'var(--border)' } };
  const total = data.reduce((s, d) => s + Number(d.count), 0);
  if (!total) return <div className="dash-empty">ไม่มีข้อมูล</div>;
  return (
    <div className="status-chart">
      {data.map(d => {
        const cfg = map[d.status] || { label: d.status, color: 'var(--border)' };
        return (
          <div key={d.status} className="status-row">
            <div className="status-row-label">
              <span className="status-row-dot" style={{ background: cfg.color }} />
              {cfg.label}
            </div>
            <div className="status-row-bar-wrap">
              <div className="status-row-bar" style={{ width: `${pct(Number(d.count), total)}%`, background: cfg.color }} />
            </div>
            <div className="status-row-count">{fmt(d.count)}</div>
          </div>
        );
      })}
      <div className="status-total">ทั้งหมด {fmt(total)} สนทนา</div>
    </div>
  );
}

// ── Agent Activity ─────────────────────────────────────
function AgentActivity({ data }) {
  const active = data.filter(a => Number(a.messages_sent) > 0);
  if (!active.length) return <div className="dash-empty">ยังไม่มีข้อมูลในช่วงเวลานี้</div>;
  const maxMsgs = Math.max(...active.map(a => Number(a.messages_sent)), 1);
  return (
    <div className="agent-act-list">
      {active.map((a, i) => (
        <div key={a.id} className="agent-act-row">
          <div className="agent-act-rank">#{i + 1}</div>
          <div className="agent-act-avatar">{a.name.slice(0, 2)}</div>
          <div className="agent-act-info">
            <div className="agent-act-name">{a.name}</div>
            <div className="agent-act-sub">{fmt(a.conversations_handled)} สนทนา</div>
          </div>
          <div className="agent-act-bar-wrap">
            <div className="agent-act-bar" style={{ width: `${pct(Number(a.messages_sent), maxMsgs)}%` }} />
          </div>
          <div className="agent-act-msgs">{fmt(a.messages_sent)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();
  const { agent, logout } = useAuth();
  const [days, setDays]           = useState(7);
  const [selectedOa, setSelectedOa] = useState('all');
  const [oaList, setOaList]         = useState([]);
  const [startDate, setStartDate]   = useState('');
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => { api.getOAAccounts().then(setOaList).catch(console.error); }, []);

  function shiftDate(n) {
    const base = startDate || today;
    const d = new Date(base + 'T00:00:00');
    d.setDate(d.getDate() + n);
    const nd = d.toISOString().slice(0, 10);
    if (nd <= today) setStartDate(nd);
  }

  const load = useCallback(() => {
    setLoading(true);
    api.getDashboard({
      days,
      oaId: selectedOa !== 'all' ? selectedOa : null,
      startDate: startDate || null,
    })
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days, selectedOa, startDate]);

  useEffect(() => { load(); }, [load]);

  const ov = data?.overview || {};
  const periodLabel = startDate ? `${startDate} (${days}ว)` : (days === 1 ? 'วันนี้' : `${days}ว`);

  return (
    <div className="dash-layout">
      {/* Sidebar */}
      <aside className="settings-sidebar">
        <div className="settings-logo">
          <MergeChatLogo size={32} />
          <span><span>Merge</span><span style={{ color: '#FF6347', fontWeight: 700 }}>Chat</span></span>
        </div>
        <nav className="settings-nav">
          <button className="nav-item" onClick={() => navigate('/')}>💬 กล่องข้อความ</button>
          <button className="nav-item active">📊 Dashboard</button>
          <button className="nav-item" onClick={() => navigate('/reports')}>📋 Reports</button>
          <button className="nav-item" onClick={() => navigate('/settings')}>⚙️ ตั้งค่า</button>
        </nav>
        <div className="settings-agent">
          <div className="agent-info">
            <div className="agent-avatar">{agent?.name?.slice(0, 2)}</div>
            <div>
              <div className="agent-name">{agent?.name}</div>
              <div className="agent-role">{agent?.role}</div>
            </div>
          </div>
          <button className="btn-logout-sm" onClick={logout}>ออก</button>
        </div>
      </aside>

      {/* Main */}
      <main className="dash-main">
        {/* Header */}
        <div className="dash-header">
          <div>
            <h1 className="dash-title">Dashboard</h1>
            <p className="dash-subtitle">ภาพรวมการใช้งานระบบ</p>
          </div>
          <div className="dash-controls">
            {/* OA Selector */}
            <select className="dash-oa-select" value={selectedOa} onChange={e => setSelectedOa(e.target.value)}>
              <option value="all">ทุก OA</option>
              {oaList.map(oa => <option key={oa.id} value={oa.id}>{oa.name}</option>)}
            </select>
            {/* Date picker + nav */}
            <div className="dash-date-wrap">
              <button className="dash-nav-btn" onClick={() => shiftDate(-days)} title="ย้อนหลัง">◀</button>
              <input type="date" className="dash-date-input" value={startDate}
                onChange={e => setStartDate(e.target.value)} max={today} />
              <button className="dash-nav-btn" onClick={() => shiftDate(days)}
                disabled={!startDate || startDate >= today} title="ถัดไป">▶</button>
              {startDate && <button className="dash-nav-clear" onClick={() => setStartDate('')} title="ล้างวันที่">✕</button>}
            </div>
            {/* Period buttons */}
            {[{label:'วันนี้',val:1},{label:'7 วัน',val:7},{label:'30 วัน',val:30},{label:'90 วัน',val:90}].map(({label,val}) => (
              <button key={val} className={`dash-period ${days === val ? 'active' : ''}`} onClick={() => setDays(val)}>
                {label}
              </button>
            ))}
            <button className="dash-refresh" onClick={load} title="Refresh">↻</button>
          </div>
        </div>

        {loading ? (
          <div className="dash-loading">กำลังโหลด...</div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="dash-stats">
              <StatCard icon="📨" label={`ข้อความเข้า (${periodLabel})`} value={ov.inbound} trend={ov.inbound_trend} color="var(--accent)" />
              <StatCard icon="📤" label={`ข้อความออก (${periodLabel})`} value={ov.outbound} trend={ov.outbound_trend} color="var(--accent-dark)" />
              <StatCard icon="💬" label="สนทนาทั้งหมด" value={ov.active_conversations} color="#5B8DEF" />
              <StatCard icon="✨" label={`สนทนาใหม่ (${periodLabel})`} value={ov.new_conversations} color="#FFB347" />
              <StatCard icon="🌟" label={`ลูกค้าใหม่ (${periodLabel})`} value={ov.new_members} trend={ov.new_members_trend} color="#a855f7" />
            </div>

            {/* Bar Chart */}
            <div className="dash-card">
              <div className="dash-card-title">ปริมาณข้อความรายวัน</div>
              <BarChart data={data?.messages_by_day || []} days={days} startDate={startDate} />
            </div>

            {/* 2-col grid */}
            <div className="dash-grid">
              <div className="dash-card">
                <div className="dash-card-title">OA Performance</div>
                <OaBreakdown data={data?.oa_breakdown || []} />
              </div>
              <div className="dash-card">
                <div className="dash-card-title">Peak Hours — ชั่วโมงยอดนิยม</div>
                <PeakHours data={data?.peak_hours || []} />
              </div>
              <div className="dash-card">
                <div className="dash-card-title">Status Distribution</div>
                <StatusChart data={data?.status_breakdown || []} />
              </div>
              <div className="dash-card">
                <div className="dash-card-title">Team Activity</div>
                <AgentActivity data={data?.agent_stats || []} />
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
